import React, { useState, useEffect } from 'react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { inventoryService } from '../../services/inventoryService';
import ItemDetailModal from './components/ItemDetailModal';
import ItemFormModal from './components/ItemFormModal';
import ArchiveDeleteModal from './components/ArchiveDeleteModal';
import {
  Warehouse, RefreshCw, AlertTriangle, Edit2, Plus, Wine, Package,
  TrendingDown, TrendingUp, CheckCircle, X, Eye, Archive, Search,
  TrendingUp as EuroIcon
} from 'lucide-react';

export default function UnifiedInventoryManagement() {
  const [barrelInventory, setBarrelInventory] = useState([]);
  const [cartonInventory, setCartonInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  // Modals
  const [detailItem, setDetailItem] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [archiveItem, setArchiveItem] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [barrelInv, cartonInv] = await Promise.all([
        inventoryService?.getBarrelInventory(),
        inventoryService?.getCartonInventory()
      ]);
      setBarrelInventory(barrelInv || []);
      setCartonInventory(cartonInv || []);
    } catch (e) {
      setError('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const showSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  };

  const filterItems = (items) => {
    return items?.filter(item => {
      const matchSearch = !search ||
        item?.name?.toLowerCase()?.includes(search?.toLowerCase()) ||
        item?.sku?.toLowerCase()?.includes(search?.toLowerCase()) ||
        item?.cartonType?.toLowerCase()?.includes(search?.toLowerCase()) ||
        item?.barrelType?.toLowerCase()?.includes(search?.toLowerCase());
      const matchArchived = showArchived ? true : item?.status !== 'archived';
      return matchSearch && matchArchived;
    });
  };

  const filteredBarrels = filterItems(barrelInventory);
  const filteredCartons = filterItems(cartonInventory);

  const allItems = [...barrelInventory, ...cartonInventory];
  const activeItems = allItems?.filter(i => i?.status !== 'archived');
  const totalBarrelStock = barrelInventory?.filter(i => i?.status !== 'archived')?.reduce((sum, i) => sum + (i?.stockQuantity || 0), 0);
  const totalCartonStock = cartonInventory?.filter(i => i?.status !== 'archived')?.reduce((sum, i) => sum + (i?.stockQuantity || 0), 0);
  const lowBarrels = barrelInventory?.filter(i => i?.status !== 'archived' && (i?.stockQuantity || 0) <= (i?.reorderThreshold || 10))?.length;
  const lowCartons = cartonInventory?.filter(i => i?.status !== 'archived' && (i?.stockQuantity || 0) <= (i?.reorderThreshold || i?.minStockLevel || 10))?.length;
  const totalCA = activeItems?.reduce((sum, i) => sum + (i?.stockQuantity || 0) * (i?.salePrice || i?.priceEuros || i?.unitPriceSell || 0), 0);

  const getStockStatus = (current, threshold) => {
    if (current === 0) return { label: 'Rupture', color: 'bg-red-100 text-red-700', icon: <TrendingDown className="h-3 w-3" /> };
    if (current <= threshold) return { label: 'Bas', color: 'bg-yellow-100 text-yellow-700', icon: <AlertTriangle className="h-3 w-3" /> };
    return { label: 'OK', color: 'bg-green-100 text-green-700', icon: <TrendingUp className="h-3 w-3" /> };
  };

  const handleArchiveDone = (action) => {
    showSuccess(action === 'archived' ? 'Article archivé avec succès' : 'Article supprimé avec succès');
    fetchData();
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Warehouse className="h-7 w-7 text-green-600" />
              Inventaire Centralisé — Barriques & Cartons
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">Vue unifiée des stocks barriques et cartons</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={fetchData} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700">
              <RefreshCw className="h-4 w-4" /> Actualiser
            </button>
            <button
              onClick={() => setShowNewForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
            >
              <Plus className="h-4 w-4" /> Nouvel article
            </button>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> {error}
            <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
            <CheckCircle className="h-4 w-4" /> {success}
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700">
            <div className="flex items-center gap-2 mb-1">
              <Wine className="h-4 w-4 text-amber-600" />
              <p className="text-xs text-gray-500 dark:text-gray-400">Stock Barriques</p>
            </div>
            <p className="text-2xl font-bold text-amber-600">{totalBarrelStock}</p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700">
            <div className="flex items-center gap-2 mb-1">
              <Package className="h-4 w-4 text-blue-600" />
              <p className="text-xs text-gray-500 dark:text-gray-400">Stock Cartons</p>
            </div>
            <p className="text-2xl font-bold text-blue-600">{totalCartonStock}</p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700">
            <div className="flex items-center gap-2 mb-1">
              <EuroIcon className="h-4 w-4 text-emerald-600" />
              <p className="text-xs text-gray-500 dark:text-gray-400">CA Total</p>
            </div>
            <p className="text-2xl font-bold text-emerald-600">
              {totalCA?.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €
            </p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">Alertes Barriques</p>
            <p className={`text-2xl font-bold ${lowBarrels > 0 ? 'text-red-500' : 'text-green-600'}`}>{lowBarrels}</p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">Alertes Cartons</p>
            <p className={`text-2xl font-bold ${lowCartons > 0 ? 'text-red-500' : 'text-green-600'}`}>{lowCartons}</p>
          </div>
        </div>

        {/* Search + Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par nom, SKU, type..."
              value={search}
              onChange={e => setSearch(e?.target?.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-400"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
            <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e?.target?.checked)} className="rounded" />
            Afficher archivés
          </label>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">Chargement...</div>
        ) : (
          <div className="space-y-8">

            {/* Barrel Inventory */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-4">
                <Wine className="h-5 w-5 text-amber-600" />
                Inventaire Barriques
                <span className="text-sm font-normal text-gray-500 dark:text-gray-400">({filteredBarrels?.length} article{filteredBarrels?.length !== 1 ? 's' : ''})</span>
              </h2>
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-slate-700 border-b border-gray-200 dark:border-slate-600">
                      <tr>
                        <th className="text-left px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Article</th>
                        <th className="text-right px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Stock</th>
                        <th className="text-right px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Seuil</th>
                        <th className="text-right px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Prix achat</th>
                        <th className="text-right px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Prix vente</th>
                        <th className="text-center px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Statut</th>
                        <th className="text-center px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                      {filteredBarrels?.length === 0 ? (
                        <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400 dark:text-gray-500">Aucun article</td></tr>
                      ) : filteredBarrels?.map(item => {
                        const stock = item?.stockQuantity || 0;
                        const threshold = item?.reorderThreshold || 10;
                        const status = getStockStatus(stock, threshold);
                        const isArchived = item?.status === 'archived';
                        return (
                          <tr key={item?.id} className={`hover:bg-gray-50 dark:hover:bg-slate-700/50 ${isArchived ? 'opacity-50' : ''}`}>
                            <td className="px-4 py-3">
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">{item?.name || item?.barrelType}</p>
                                {item?.sku && <p className="text-xs text-gray-400">{item?.sku}</p>}
                                {isArchived && <span className="text-xs text-orange-500">Archivé</span>}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-white">{stock}</td>
                            <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">{threshold}</td>
                            <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">{(item?.purchasePrice || 0)?.toFixed(2)} €</td>
                            <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">{(item?.priceEuros || item?.salePrice || 0)?.toFixed(2)} €</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${status?.color}`}>
                                {status?.icon} {status?.label}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <button onClick={() => setDetailItem(item)} title="Voir détail" className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded">
                                  <Eye className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={() => setEditItem(item)} title="Modifier" className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded">
                                  <Edit2 className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={() => setArchiveItem(item)} title="Archiver/Supprimer" className="p-1.5 text-gray-500 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded">
                                  <Archive className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Carton Inventory */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-4">
                <Package className="h-5 w-5 text-blue-600" />
                Inventaire Cartons
                <span className="text-sm font-normal text-gray-500 dark:text-gray-400">({filteredCartons?.length} article{filteredCartons?.length !== 1 ? 's' : ''})</span>
              </h2>
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-slate-700 border-b border-gray-200 dark:border-slate-600">
                      <tr>
                        <th className="text-left px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Article</th>
                        <th className="text-left px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Dimensions</th>
                        <th className="text-right px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Stock</th>
                        <th className="text-right px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Stock min</th>
                        <th className="text-right px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Prix achat</th>
                        <th className="text-right px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Prix vente</th>
                        <th className="text-center px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Statut</th>
                        <th className="text-center px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                      {filteredCartons?.length === 0 ? (
                        <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-400 dark:text-gray-500">Aucun article</td></tr>
                      ) : filteredCartons?.map(item => {
                        const stock = item?.stockQuantity || 0;
                        const threshold = item?.reorderThreshold || item?.minStockLevel || 10;
                        const status = getStockStatus(stock, threshold);
                        const isArchived = item?.status === 'archived';
                        return (
                          <tr key={item?.id} className={`hover:bg-gray-50 dark:hover:bg-slate-700/50 ${isArchived ? 'opacity-50' : ''}`}>
                            <td className="px-4 py-3">
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">{item?.name || item?.cartonType}</p>
                                {item?.sku && <p className="text-xs text-gray-400">{item?.sku}</p>}
                                {isArchived && <span className="text-xs text-orange-500">Archivé</span>}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">{item?.dimensions || '—'}</td>
                            <td className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-white">{stock}</td>
                            <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">{item?.minStockLevel || 10}</td>
                            <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">{(item?.purchasePrice || item?.unitPriceBuy || 0)?.toFixed(2)} €</td>
                            <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">{(item?.unitPriceSell || item?.salePrice || 0)?.toFixed(2)} €</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${status?.color}`}>
                                {status?.icon} {status?.label}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <button onClick={() => setDetailItem(item)} title="Voir détail" className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded">
                                  <Eye className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={() => setEditItem(item)} title="Modifier" className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded">
                                  <Edit2 className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={() => setArchiveItem(item)} title="Archiver/Supprimer" className="p-1.5 text-gray-500 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded">
                                  <Archive className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Modals */}
      {detailItem && (
        <ItemDetailModal item={detailItem} onClose={() => setDetailItem(null)} />
      )}
      {(showNewForm || editItem) && (
        <ItemFormModal
          item={editItem}
          onClose={() => { setShowNewForm(false); setEditItem(null); }}
          onSaved={() => { showSuccess(editItem ? 'Article modifié avec succès' : 'Article créé avec succès'); fetchData(); }}
        />
      )}
      {archiveItem && (
        <ArchiveDeleteModal
          item={archiveItem}
          onClose={() => setArchiveItem(null)}
          onDone={handleArchiveDone}
        />
      )}
    </div>
  );
}
